import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, MessageSquare, PenLine, Sparkles } from "lucide-react";
import AIChat from "../../components/AIChat";
import MiniSitePreview from "../../components/MiniSitePreview";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import { errMsg } from "../../lib/api";

const EXAMPLE = "e.g. I run a small bakery in Pune selling custom cakes and cookies. I want people to see our menu, order cakes for birthdays with a deposit, and contact us on WhatsApp.";

export function StepDiscovery(props) {
  const { project, go } = props;
  const [mode, setMode] = useState("chat");
  const tab = (k, l, Icon) => <button onClick={() => setMode(k)} className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${mode === k ? "bg-[#0b3d2e] text-[#f6ecd2]" : "text-slate-600 hover:bg-[#f4efe2]"}`} data-testid={`discovery-mode-${k}`}><Icon className="w-4 h-4" />{l}</button>;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border bg-white p-1">{tab("chat", "Chat with AI", MessageSquare)}{tab("write", "Write a description", PenLine)}</div>
        {project.discovery_text && <button onClick={() => go(1)} className="btn-emerald !py-2" data-testid="discovery-next">Continue to quick questions <ArrowRight className="w-4 h-4" /></button>}
      </div>
      {mode === "chat" ? (
        <div className="grid lg:grid-cols-12 gap-8">
          <AIChat project={project} api={props.api} onChange={props.onChat} className="lg:col-span-8 h-[620px]" />
          <aside className="lg:col-span-4 space-y-4 text-sm text-slate-600">
            <MiniSitePreview project={project} api={props.api} />
            <div className="card-lux bg-[#f4efe2]"><h3 className="text-xl text-[#062a20]">How the assistant helps</h3>
              <ul className="mt-3 space-y-2 list-disc pl-5"><li>Describe your idea in your own words — even complex ones.</li><li>Attach drawings, PDFs, spreadsheets or images.</li><li>It asks only what matters, fills in the quick questions for you and adds matching features with fixed prices.</li></ul></div>
            <p className="text-xs">Everything is saved to your project. You can switch to the quick questions any time.</p>
          </aside>
        </div>) : <WriteDiscovery {...props} />}
    </div>);
}

function WriteDiscovery({ project, api, reload, go }) {
  const [text, setText] = useState(project.discovery_text || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (text.trim().length < 20) return toast.error("Please write at least a couple of sentences (20+ characters) so we can understand your business.");
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/discovery`, { text });
      if (!r.data.ai_ok) toast.message("Our AI assistant is unavailable right now — we'll ask a few more questions instead.");
      await reload(); go(1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7 card-lux !p-8">
        <p className="eyebrow">Step 1</p>
        <h2 className="mt-2 text-3xl text-[#062a20]">Tell us about your business</h2>
        <p className="mt-2 text-sm text-slate-600">Write naturally — what you do, who your customers are, and what you'd like the website to help with. You can correct anything we pick up.</p>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={EXAMPLE} className="mt-5 text-base" data-testid="discovery-textarea" aria-label="Describe your business" />
        <div className="mt-2 flex justify-between text-xs text-slate-500"><span>Please don't include passwords or sensitive personal data.</span><span>{text.length}/5000</span></div>
        <button onClick={submit} disabled={busy} className="btn-emerald mt-6" data-testid="discovery-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Understanding your business…</> : <><Sparkles className="w-4 h-4" /> Continue</>}
        </button>
      </div>
      <aside className="lg:col-span-5 space-y-4 text-sm text-slate-600">
        <div className="card-lux bg-[#f4efe2]"><h3 className="text-xl text-[#062a20]">What happens next</h3>
          <ul className="mt-3 space-y-2 list-disc pl-5"><li>We ask only the questions that matter for you.</li><li>You confirm a short business profile.</li><li>We recommend features and show three packages with prices.</li></ul></div>
        <p className="text-xs">Your answers are saved as you go. You can leave and come back any time.</p>
      </aside>
    </div>
  );
}

export function StepQuestions({ project, api, reload, go, openChat }) {
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  useEffect(() => { api.get(`/projects/${project.id}/next-question`).then((r) => setState(r.data)); }, [api, project.id]);
  if (!state) return <p className="text-sm text-slate-500">Loading…</p>;
  const q = state.next_question;
  const chosen = picked !== null ? picked : state.selected;
  const send = async (value, skip = false) => {
    setPicked(skip ? null : value); setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/answer${skip ? "/skip" : ""}`, { key: q.key, value });
      setState({ ...r.data, selected: null }); setText("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setPicked(null); }
  };
  const back = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/answer/back`);
      setState({ ...r.data, total: state.total });
      setText(typeof r.data.selected === "string" ? r.data.selected : "");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const Opt = ({ value, label, testid }) => {
    const on = chosen === value;
    return (
      <button disabled={busy} onClick={() => send(value)} aria-pressed={on} data-testid={testid}
        className={`min-w-[110px] px-5 py-3 rounded-md border text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors duration-150 ${on ? "bg-[#0b3d2e] text-[#f6ecd2] border-[#0b3d2e] ring-2 ring-[#b8892b] ring-offset-2" : "bg-white text-[#0b3d2e] border-[#0b3d2e]/25 hover:border-[#0b3d2e] hover:bg-[#0b3d2e]/5"}`}>
        {on && (busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />)}{label}
      </button>);
  };
  const pct = state.total ? Math.round((state.answered / state.total) * 100) : 100;
  return (
    <div className="max-w-2xl">
      <Progress value={q ? pct : 100} className="h-1.5" aria-label="Question progress" />
      <p className="mt-2 text-xs text-slate-500" data-testid="questions-progress">{q ? `${state.answered} answered — only relevant questions are shown` : "All done"}{openChat && <> · <button onClick={openChat} className="underline text-[#0b3d2e]" data-testid="questions-open-chat">Prefer to explain in a chat?</button></>}</p>
      {q ? (
        <div className="card-lux !p-8 mt-6" data-testid={`question-${q.key}`}>
          <h2 className="text-2xl md:text-3xl text-[#062a20]" data-testid="question-text">{q.text}</h2>
          {q.help && <p className="mt-2 text-sm text-slate-500">{q.help}</p>}
          <div className="mt-6 flex flex-wrap gap-3">
            {q.type === "yesno" && <><Opt value={true} label="Yes" testid="answer-yes" /><Opt value={false} label="No" testid="answer-no" /></>}
            {q.type === "choice" && q.options.map((o, i) => <Opt key={o} value={o} label={o} testid={`answer-option-${i}`} />)}
            {q.type === "text" && <form className="flex w-full gap-3" onSubmit={(e) => { e.preventDefault(); send(text); }}>
              <Input value={text} onChange={(e) => setText(e.target.value)} className="flex-1 h-11" data-testid="answer-text-input" aria-label={q.text} autoFocus />
              <button disabled={busy || !text.trim()} className="btn-emerald" data-testid="answer-text-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Next</button></form>}
          </div>
          <div className="mt-6 flex justify-between text-sm">
            <button onClick={back} disabled={!state.answered} className="inline-flex items-center gap-1 text-slate-500 disabled:opacity-40" data-testid="question-back"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button onClick={() => send(null, true)} disabled={busy} className="text-slate-500 underline" data-testid="question-skip">Skip — I'm not sure</button>
          </div>
        </div>
      ) : (
        <div className="card-lux !p-8 mt-6" data-testid="questions-done">
          <h2 className="text-3xl text-[#062a20]">Thank you — that's everything we need.</h2>
          <p className="mt-2 text-sm text-slate-600">Next, check your business profile and correct anything we got wrong.</p>
          <div className="mt-6 flex gap-3"><button onClick={back} className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="questions-done-back"><ArrowLeft className="w-4 h-4" /> Change last answer</button>
            <button onClick={async () => { await reload(); go(2); }} className="btn-emerald" data-testid="questions-continue">Review my profile <ArrowRight className="w-4 h-4" /></button></div>
        </div>)}
    </div>
  );
}
